import React, { useContext, useRef } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLazyLoadQuery, useMutation } from "react-relay/hooks";

import useServerErrorsHandler from "src/hooks/useServerErrorsHandler";
import { DICOMTypes, SUBMIT_TYPES, UPLOAD_STATUSES } from "src/utils/constants";
import { getSelectControlObject } from "src/utils/helpers";
import AlertContext from "src/contexts/AlertContext";
import { useDicomUpload } from "src/contexts/DicomUploadContext";
import { addActiveUpload } from "src/utils/activeUploads";

import useJobRequest from "src/hooks/useJobRequest";

import AbstractJobRequestForm from "./AbstractJobRequestForm";
import SaveJobRequestMutation from "./JobRequestMutation/SaveJobRequestMutation";
import FinalizeUploadMutation from "./FinalizeUploadMutation/FinalizeUploadMutation";
import MarkJobRequestUploadFailedMutation from "./JobRequestMutation/MarkJobRequestUploadFailedMutation";

import ModalitiesExamTypesQuery from "./JobRequestQuery/JobRequestQuery";
import DeleteJobRequestModal from "./JobRequestQuery/DeleteJobRequestModal";

import "../../assets/scss/SharedStyle/FormStyle.scss";
import "./JobRequestStyle.scss";

const CompleteJobRequestForm = ({
  setformErrors,
  setdisplayFormErrors,
  currentJobRequest,
  abilities,
}) => {
  const { t } = useTranslation([
    "jobRequests",
    "errors",
    "translation",
    "uploader",
  ]);
  const navigate = useNavigate();

  const [saveAsDraft, isSaving] = useMutation(SaveJobRequestMutation);
  const [finalizeUpload] = useMutation(FinalizeUploadMutation);
  const [markUploadFailed] = useMutation(MarkJobRequestUploadFailedMutation);
  const { startUpload } = useDicomUpload();
  const dicomFileRef = useRef(null);

  const { dicom, dicomType, dicomContentSection } =
    useJobRequest(currentJobRequest);

  const { modalities, examTypes, serverErrors } = useLazyLoadQuery(
    ModalitiesExamTypesQuery,
  );

  if (serverErrors) {
    useServerErrorsHandler(serverErrors);
    return null;
  }

  const { showAlert } = useContext(AlertContext);

  const normalizeModalityName = (name) => String(name || "").trim().toLowerCase();
  const buildModalityCandidates = (rawModality) => String(rawModality || "")
    .split(/[\\/,]/)
    .map((name) => normalizeModalityName(name))
    .filter(Boolean);
  const modalityCandidates = buildModalityCandidates(currentJobRequest?.modality);
  const matchedModality =
    currentJobRequest?.examType?.modality
    || modalities.find(
      ({ name }) => modalityCandidates.includes(normalizeModalityName(name)),
    );
  const initialExamTypes = matchedModality
    ? examTypes.filter((examType) => examType.modality.dbId === matchedModality.dbId)
    : [];

  const dicomAttachment =
    dicom && dicomType === DICOMTypes.FILE ? { dicom } : {};
  const requestAttachments = currentJobRequest?.attachments || [];
  const initialAttachmentsAttributes = [
    dicomAttachment,
    { attachments: requestAttachments },
  ];

  const initialValues = {
    patientFullname: currentJobRequest?.patientFullname || "",
    patientId: currentJobRequest?.originalSyncData?.patient_id || "",
    patientAge: currentJobRequest?.patientAge || "",
    patientAgeUnit: currentJobRequest
      ? getSelectControlObject(currentJobRequest.patientAgeUnit)
      : getSelectControlObject("years", "Years"),
    patientClinicalData: currentJobRequest?.patientClinicalData || "",
    referringDoctor: currentJobRequest?.referringDoctor || "",
    patientGender: currentJobRequest?.patientGender || "male",
    modalityId: matchedModality
      ? getSelectControlObject(
        matchedModality.dbId,
        matchedModality.name,
      )
      : "",
    examTypeId: currentJobRequest?.examType
      ? getSelectControlObject(
        currentJobRequest.examType.dbId,
        currentJobRequest.examType.name,
      )
      : "",
    urgency: currentJobRequest?.urgency || "normal",
    dicomRadio: dicomType === DICOMTypes.URL ? "Url" : "File",
    patientAdditionalInfo: currentJobRequest?.patientAdditionalInfo || "",
    dicom:
      currentJobRequest?.dicom && dicomType === DICOMTypes.URL
        ? currentJobRequest?.dicom
        : "",
    examTypes: initialExamTypes,
    modalities,
    attachmentsAttributes: initialAttachmentsAttributes,
    contrast: currentJobRequest.contrast === "With" || false,
    duplicateRequest: false,
    requirePreviousStudy: false,
    submitType: SUBMIT_TYPES.SAVE_DRAFT,
    // Draft has valid DICOM on server, or upload in progress/completed (allow completing).
    // When upload completed, DICOM is on server even if API returns null for current user.
    existingDicom: !!(dicom && (dicomType === DICOMTypes.FILE || dicomType === DICOMTypes.URL))
      || (currentJobRequest?.uploadStatus === UPLOAD_STATUSES.initiated
        || currentJobRequest?.uploadStatus === UPLOAD_STATUSES.not_started
        || currentJobRequest?.uploadStatus === UPLOAD_STATUSES.completed),
  };

  const allowToCreateJobRequest = (values) => {
    const formValid =
      values.patientFullname &&
      values.patientClinicalData &&
      values.modalityId &&
      values.examTypeId;
    if (!formValid) return false;

    // Show "Create job request" only when DICOM is ready (upload completed),
    // not while upload is in progress (orthanc/auto-uploader).
    const uploadInProgress =
      currentJobRequest?.uploadStatus === UPLOAD_STATUSES.initiated ||
      currentJobRequest?.uploadStatus === UPLOAD_STATUSES.not_started;
    if (dicomContentSection && uploadInProgress) return false;

    return true;
  };

  const handleSuccessCreatedJobRequest = (
    JobRequestId,
    submitType,
    { resetForm },
  ) => {
    const jobRequestURL = `/job-request/${JobRequestId}`;
    resetForm();
    navigate(jobRequestURL);
    showAlert(
      submitType === SUBMIT_TYPES.COMPLETE
        ? t("createSuccess")
        : t("saveDraftSuccess"),
    );
  };

  const getRequestInput = (values) => {
    const deletedAttachmentsList =
      values.attachmentsAttributes.attachmentsDeleted;
    const deletedDicomFile = values.attachmentsAttributes.dicomDeleted;

    const dicomFile =
      values.attachmentsAttributes?.dicom ?? values.attachmentsAttributes?.[0]?.dicom;
    const isNewDicomFile = values.dicomRadio === "File" && dicomFile instanceof File;
    const usePresignedUrl =
      isNewDicomFile && values.submitType === SUBMIT_TYPES.COMPLETE;

    const input = {
      jobRequestId: currentJobRequest?.dbId,
      contrast: values.contrast,
      examTypeId: values.examTypeId.value,
      patientAdditionalInfo: values.patientAdditionalInfo,
      patientId: values.patientId,
      patientAge: values.patientAge ? values.patientAge : null,
      patientAgeUnit: values.patientAgeUnit?.value || null,
      patientClinicalData: values.patientClinicalData,
      patientFullname: values.patientFullname,
      patientGender: values.patientGender,
      referringDoctor: values.referringDoctor,
      urgency: values.urgency,
      isComplete: values.submitType === SUBMIT_TYPES.COMPLETE,
      requirePreviousStudy: values.requirePreviousStudy,
      // Always send dicom URL when user has chosen URL mode with a value
      ...(values.dicomRadio === "Url" && values.dicom ? { dicom: values.dicom } : {}),
      ...(deletedAttachmentsList
        ? { attachmentsDeleted: deletedAttachmentsList }
        : {}),
      ...(deletedDicomFile ? { dicomDeleted: deletedDicomFile } : {}),
    };

    if (usePresignedUrl && dicomFile) {
      input.files = [
        {
          filename: dicomFile.name,
          contentType: dicomFile.type || "application/zip",
          size: dicomFile.size,
          uploadType: "dicom_url",
        },
      ];
    }

    return input;
  };

  const getRequestUploadables = (values) => {
    const dicomFile =
      values.attachmentsAttributes?.dicom ?? values.attachmentsAttributes?.[0]?.dicom;
    const usePresignedUrlForDicom =
      values.dicomRadio === "File" &&
      dicomFile instanceof File &&
      values.submitType === SUBMIT_TYPES.COMPLETE;

    const uploadables = {};

    Object.entries(values.attachmentsAttributes).forEach(([key, value]) => {
      if (
        key === "dicom" &&
        values.dicomRadio === "File" &&
        value instanceof File &&
        !usePresignedUrlForDicom
      ) {
        uploadables[key] = value;
      } else if (key === "dicom" && usePresignedUrlForDicom) {
        // DICOM uploaded via presigned URL, not in form body
      } else if (key === "attachments") {
        uploadables[key] = value.filter(
          (attachment) => attachment instanceof File,
        );
      }
    });
    return Object.keys(uploadables).length ? uploadables : null;
  };

  const displayFormErrors = (errors) => {
    window.scroll(0, 0);
    setformErrors(errors);
    setdisplayFormErrors(true);
  };

  const onErrorCallBack = (responseErrors) => {
    // eslint-disable-next-line no-console
    console.error('onErrorCallBack', responseErrors);

    const typeErrors = responseErrors?.extensions?.problems;
    const formattedErrors = [];

    if (typeErrors) {
      Object.values(typeErrors).forEach((fieldError) => {
        formattedErrors.push({
          field: fieldError.path?.[0],
          message: fieldError?.explanation,
        });
      });
    } else {
      // Fallback for generic errors
      formattedErrors.push({
        field: 'global',
        message: responseErrors?.message || 'An unknown error occurred.',
      });
    }

    displayFormErrors(formattedErrors);
  };

  const onSubmit = (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    setformErrors([]);
    setdisplayFormErrors(false);

    const input = getRequestInput(values);
    const uploadables = getRequestUploadables(values);

    const dicomFile =
      values.attachmentsAttributes?.dicom ?? values.attachmentsAttributes?.[0]?.dicom;
    const usePresignedUrl =
      values.dicomRadio === "File" &&
      dicomFile instanceof File &&
      values.submitType === SUBMIT_TYPES.COMPLETE;

    if (usePresignedUrl && dicomFile) {
      dicomFileRef.current = dicomFile;
    }

    saveAsDraft({
      variables: { input },
      uploadables,
      onCompleted: ({ saveJobRequest }) => {
        setSubmitting(false);
        const { jobRequest, errors, presignedUrl } = saveJobRequest;

        if (jobRequest !== null) {
          if (presignedUrl && dicomFileRef.current) {
            const storedFile = dicomFileRef.current;
            dicomFileRef.current = null;
            addActiveUpload(jobRequest.dbId);
            const onUploadFailed = (id) => {
              markUploadFailed({
                variables: {
                  input: { jobRequestId: id },
                },
              });
            };
            startUpload(
              jobRequest.dbId,
              presignedUrl,
              storedFile,
              finalizeUpload,
              onUploadFailed,
            );
          }
          handleSuccessCreatedJobRequest(jobRequest.dbId, values.submitType, {
            resetForm,
          });
        } else if (errors.length > 0) {
          displayFormErrors(errors);
        }
      },
      onError: (responseErrors) => {
        setSubmitting(false);
        onErrorCallBack(responseErrors);
      },
    });
  };

  const isUploadFailed = currentJobRequest?.uploadStatus === UPLOAD_STATUSES.failed;

  return (
    <AbstractJobRequestForm
      initialValues={initialValues}
      examTypes={examTypes}
      onSubmit={onSubmit}
      dicomContentSection={dicomContentSection}
      allowToDuplicateJobRequest={false}
      allowToSaveDraft={!isUploadFailed}
      isLoadingProgress={isSaving}
      allowToCreateJobRequest={allowToCreateJobRequest}
      DeleteJobRequestModal={
        <DeleteJobRequestModal currentJobRequest={currentJobRequest} />
      }
      allowToDeleteJobRequest={abilities.canDeleteJobRequest}
    />
  );
};

CompleteJobRequestForm.propTypes = {
  currentJobRequest: PropTypes.instanceOf(Object),
  setformErrors: PropTypes.func,
  setdisplayFormErrors: PropTypes.func,
  abilities: PropTypes.instanceOf(Object).isRequired,
};

CompleteJobRequestForm.defaultProps = {
  currentJobRequest: null,
  setformErrors: () => { },
  setdisplayFormErrors: () => { },
};

export default CompleteJobRequestForm;
